import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import TitleWithIcon from "../../../components/layout/pageTitle/PageTitle";
import templateIcon from "../../../media/template_plan.png";
import "../../../components/AIChat/AIChat.css";
import "./PatientAnalysesAI.css";
import "../patients.css";

function mapServerMessages(list) {
    if (!Array.isArray(list)) return [];
    return list.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content ?? "",
    }));
}

function resolveApiError(data, fallback, t) {
    if (!data) return fallback;
    if (typeof data !== "object") return fallback;
    if (data.isAxiosError || data.response || data.config) {
        const apiMsg =
            data.response?.data?.request?.message ??
            data.response?.data?.message ??
            data.response?.data?.error;
        if (apiMsg) return String(apiMsg);
        if (data.message) return String(data.message);
        return t("analysesAi.networkError");
    }
    if (data.message) return String(data.message);
    return fallback;
}

function extractAssistantReply(result) {
    if (!result || typeof result !== "object") return "";
    const fromChoices = result?.choices?.[0]?.message?.content;
    if (fromChoices) return String(fromChoices);
    const messages = Array.isArray(result.messages) ? result.messages : [];
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    return lastAssistant?.content ? String(lastAssistant.content) : "";
}

function serverHasAssistantReply(serverMessages, userText) {
    const trimmed = userText.trim();
    if (!trimmed || !Array.isArray(serverMessages) || serverMessages.length === 0) {
        return false;
    }
    for (let i = serverMessages.length - 1; i >= 0; i -= 1) {
        if (serverMessages[i].role !== "user") continue;
        if ((serverMessages[i].content ?? "").trim() !== trimmed) continue;
        const next = serverMessages[i + 1];
        return Boolean(next && next.role === "assistant" && (next.content ?? "").trim());
    }
    return false;
}

async function loadPatientAiMessages(idPatient, t) {
    const data = await GetData({
        module: "patient",
        block: "ai_messages",
        id_patient: idPatient,
    });
    const errMsg = resolveApiError(data, null, t);
    if (errMsg) {
        return { messages: [], error: errMsg };
    }
    return { messages: mapServerMessages(data?.result?.messages), error: null };
}

const AI_COMPLETION_TIMEOUT_MS = 180000;

export default function PatientAnalysesAI({ embedded = false }) {
    const { t } = useTranslation("patient");
    const { patientId } = useParams();
    const id_patient = Number(patientId);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!id_patient) {
                setMessages([]);
                setHistoryLoading(false);
                return;
            }
            setHistoryLoading(true);
            try {
                const { messages: list, error } = await loadPatientAiMessages(id_patient, t);
                if (!cancelled) {
                    if (error) {
                        setMessages([]);
                    } else {
                        setMessages(list);
                    }
                }
            } catch {
                if (!cancelled) setMessages([]);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id_patient, t]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const handleSend = async () => {
        const text = (inputValue || "").trim();
        if (!text || loading || historyLoading || clearing || !id_patient) return;

        setInputValue("");
        setMessages((prev) => [
            ...prev,
            { id: `local-${Date.now()}`, role: "user", content: text },
        ]);
        setLoading(true);

        try {
            let completionError = null;
            try {
                const data = await GetData(
                    {
                        module: "patient",
                        block: "ai_completions",
                        id_patient,
                        content: text,
                    },
                    null,
                    { timeout: AI_COMPLETION_TIMEOUT_MS },
                );
                completionError = resolveApiError(data, null, t);
                if (!completionError) {
                    const result = data?.result;
                    const fromDb = result?.messages;
                    if (Array.isArray(fromDb) && fromDb.length > 0) {
                        setMessages(mapServerMessages(fromDb));
                        return;
                    }
                    const assistantReply = extractAssistantReply(result);
                    if (assistantReply) {
                        setMessages((prev) => [
                            ...prev,
                            { role: "assistant", content: assistantReply },
                        ]);
                        return;
                    }
                }
            } catch (e) {
                completionError = e.message || t("analysesAi.unknown");
            }

            // Ответ мог сохраниться в БД, даже если клиент не получил JSON (таймаут прокси и т.п.)
            const syncDelays = [0, 2000, 5000];
            for (const delayMs of syncDelays) {
                if (delayMs > 0) {
                    await new Promise((resolve) => setTimeout(resolve, delayMs));
                }
                const { messages: serverMessages, error: historyError } = await loadPatientAiMessages(id_patient, t);
                if (historyError) continue;
                if (serverHasAssistantReply(serverMessages, text)) {
                    setMessages(serverMessages);
                    return;
                }
            }

            setInputValue(text);
            const { messages: latestMessages } = await loadPatientAiMessages(id_patient, t);
            setMessages([
                ...latestMessages,
                {
                    role: "assistant",
                    content: t("error", {
                        ns: "common",
                        message: completionError || t("analysesAi.noReply"),
                    }),
                },
            ]);
        } catch (e) {
            setInputValue(text);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: t("analysesAi.errorRequest", {
                        message: e.message || t("analysesAi.unknown"),
                    }),
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClearChat = async () => {
        if (!id_patient || loading || historyLoading || clearing) return;
        const confirmed = window.confirm(t("analysesAi.confirmClear"));
        if (!confirmed) return;

        setClearing(true);
        try {
            const data = await GetData({
                module: "patient",
                block: "ai_clear",
                id_patient,
            });
            const clearError = resolveApiError(data, null, t);
            if (clearError) {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `err-${Date.now()}`,
                        role: "assistant",
                        content: t("error", { ns: "common", message: clearError }),
                    },
                ]);
                return;
            }
            setMessages([]);
            setInputValue("");
            if (inputRef.current) inputRef.current.focus();
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                    id: `err-${Date.now()}`,
                    role: "assistant",
                    content: t("analysesAi.errorRequest", {
                        message: e.message || t("analysesAi.unknown"),
                    }),
                },
            ]);
        } finally {
            setClearing(false);
        }
    };

    const isDisabled = loading || historyLoading || clearing || !id_patient;

    const chatBlock = (
        <div className="page-block ai-chat-page-block">
            <div className="ai-chat-messages">
                {historyLoading && (
                    <div className="ai-chat-placeholder">{t("analysesAi.historyLoading")}</div>
                )}
                {!historyLoading && messages.length === 0 && !loading && (
                    <div className="ai-chat-placeholder">{t("analysesAi.empty")}</div>
                )}
                {messages.map((msg, i) => (
                    <div key={msg.id != null ? String(msg.id) : `m-${i}`} className={`ai-chat-msg ai-chat-msg--${msg.role}`}>
                        <div className="ai-chat-msg-content">{msg.content}</div>
                    </div>
                ))}
                {loading && (
                    <div className="ai-chat-msg ai-chat-msg--assistant ai-chat-msg--loading">
                        <div className="ai-chat-msg-content">{t("analysesAi.thinking")}</div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="ai-chat-input-wrap">
                <textarea
                    ref={inputRef}
                    className="ai-chat-input"
                    placeholder={id_patient ? t("analysesAi.placeholder") : t("analysesAi.invalidPatient")}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isDisabled}
                    rows={2}
                />
                <button
                    type="button"
                    className="ai-chat-send"
                    onClick={handleSend}
                    disabled={isDisabled || !inputValue.trim()}
                    title={t("analysesAi.send")}
                    aria-label={t("analysesAi.send")}
                >
                    <svg className="ai-chat-send-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                </button>
            </div>
        </div>
    );

    if (embedded) {
        return (
            <div className="patient-analyses-ai patient-analyses-ai--embedded ai-chat-page">
                <div className="patient-ai-chat-toolbar">
                    <button
                        type="button"
                        className="button-default danger"
                        onClick={handleClearChat}
                        disabled={isDisabled}
                    >
                        {clearing ? t("analysesAi.clearing") : t("analysesAi.clear")}
                    </button>
                </div>
                {chatBlock}
            </div>
        );
    }

    return (
        <div className="patient-analyses-ai ai-chat-page">
            <Row>
                <Col>
                    <div className="patient-name-title-sticky">
                        <BlockTitle
                            title={t("analysesAi.title")}
                            buttons={[
                                {
                                    label: clearing ? t("analysesAi.clearing") : t("analysesAi.clear"),
                                    onClick: handleClearChat,
                                    type: "danger",
                                },
                            ]}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="page-content">
                        {chatBlock}
                    </div>
                </Col>
            </Row>
        </div>
    );
}
